import "./PerformanceSnapshot.scss";
import { Metric, Metrics, CrossChannelMetrics } from "@blisspointmedia/bpm-types/dist/CrossChannel";
import React, { useState, useMemo, useContext, useCallback } from "react";
import * as R from "ramda";
import { Dropdown, DropdownToggleType, DownloadDropdown } from "../../Components";
import ChartContainer from "../../Components/ChartContainer";
import { CrossChannelContext } from "../CrossChannel";
import { downloadPNG, exportMultiSheetToExcel } from "../../utils/download-utils";
import SnapshotChart, { BarPart, BarType, DataItem } from "./SnapshotChart";
import {
  METRIC_TO_PRETTY_NAME,
  PERFORMANCE_METRICS,
  PERFORMANCE_METRIC_OPTIONS,
} from "../crossChannelConstants";
import { getMetricFormatter, MetricFormatter } from "../../utils/metric-format-utils";

interface PerformanceSnapshotProps {
  data: CrossChannelMetrics[];
  otherData: CrossChannelMetrics[];
  defaultMetric?: Metric;
}

interface TotalsByChannel {
  [channel: string]: {
    totals: Metrics;
    otherTotals: Metrics;
  };
}

const getTotalsByChannel = (
  data: CrossChannelMetrics[],
  otherData: CrossChannelMetrics[],
  toggledChannels: Record<string, boolean>,
  deliveryAndPerformanceChannels: Record<string, boolean>
) => {
  let totalsByChannel: TotalsByChannel = {};

  for (let item of data) {
    const { channel, metrics } = item;
    if (!toggledChannels[channel] || !deliveryAndPerformanceChannels[channel]) {
      continue;
    }

    totalsByChannel[channel] = totalsByChannel[channel] || {};
    totalsByChannel[channel].totals = R.mergeWith(
      R.add,
      totalsByChannel[channel].totals || {},
      metrics
    );

    // Compute running average of CPC, CPX, and ROAS when aggregating metrics.
    const newCPC =
      R.pathOr(0, [channel, "totals", "spend"], totalsByChannel) /
      R.pathOr(0, [channel, "totals", "clicks"], totalsByChannel);
    totalsByChannel[channel].totals.cpc = isFinite(newCPC) ? newCPC : 0;

    const newCPX =
      R.pathOr(0, [channel, "totals", "spend"], totalsByChannel) /
      R.pathOr(0, [channel, "totals", "volume"], totalsByChannel);
    totalsByChannel[channel].totals.cpx = isFinite(newCPX) ? newCPX : 0;

    totalsByChannel[channel].totals.roas =
      R.pathOr(0, [channel, "totals", "revenue"], totalsByChannel) /
      R.pathOr(0, [channel, "totals", "spend"], totalsByChannel);

    totalsByChannel[channel].totals.ctr =
      R.pathOr(0, [channel, "totals", "clicks"], totalsByChannel) /
      R.pathOr(0, [channel, "totals", "impressions"], totalsByChannel);

    totalsByChannel[channel].totals.clicksConversionRate =
      R.pathOr(0, [channel, "totals", "volume"], totalsByChannel) /
      R.pathOr(0, [channel, "totals", "clicks"], totalsByChannel);

    totalsByChannel[channel].totals.impsConversionRate =
      R.pathOr(0, [channel, "totals", "volume"], totalsByChannel) /
      R.pathOr(0, [channel, "totals", "impressions"], totalsByChannel);
  }

  for (let item of otherData) {
    const { channel, metrics } = item;
    if (!toggledChannels[channel] || !deliveryAndPerformanceChannels[channel]) {
      continue;
    }

    totalsByChannel[channel] = totalsByChannel[channel] || {};
    totalsByChannel[channel].otherTotals = R.mergeWith(
      R.add,
      totalsByChannel[channel].otherTotals || {},
      metrics
    );

    // Compute running average of CPX, and ROAS when aggregating metrics.
    const newCPC =
      R.pathOr(0, [channel, "otherTotals", "spend"], totalsByChannel) /
      R.pathOr(0, [channel, "otherTotals", "clicks"], totalsByChannel);
    totalsByChannel[channel].otherTotals.cpc = isFinite(newCPC) ? newCPC : 0;

    const newCPX =
      R.pathOr(0, [channel, "otherTotals", "spend"], totalsByChannel) /
      R.pathOr(0, [channel, "otherTotals", "volume"], totalsByChannel);

    totalsByChannel[channel].otherTotals.cpx = isFinite(newCPX) ? newCPX : 0;

    totalsByChannel[channel].otherTotals.roas =
      R.pathOr(0, [channel, "otherTotals", "revenue"], totalsByChannel) /
      R.pathOr(0, [channel, "otherTotals", "spend"], totalsByChannel);

    totalsByChannel[channel].otherTotals.ctr =
      R.pathOr(0, [channel, "otherTotals", "clicks"], totalsByChannel) /
      R.pathOr(0, [channel, "otherTotals", "impressions"], totalsByChannel);

    totalsByChannel[channel].otherTotals.clicksConversionRate =
      R.pathOr(0, [channel, "otherTotals", "volume"], totalsByChannel) /
      R.pathOr(0, [channel, "otherTotals", "clicks"], totalsByChannel);

    totalsByChannel[channel].otherTotals.impsConversionRate =
      R.pathOr(0, [channel, "otherTotals", "volume"], totalsByChannel) /
      R.pathOr(0, [channel, "otherTotals", "impressions"], totalsByChannel);
  }

  return totalsByChannel;
};

/**
 * Average/Sum the metrics for each channel for both date ranges.
 */
const processSnapshotData = (
  totalsByChannel: TotalsByChannel,
  toggledChannels: Record<string, boolean>,
  metric: string,
  channelColors: Record<string, string>,
  availableChannels: Record<string, boolean>,
  metricFormatter: MetricFormatter
): DataItem[] => {
  let processedData: DataItem[] = [];

  for (let channel of R.keys(availableChannels).sort()) {
    if (!toggledChannels[channel]) {
      continue;
    }

    const topValue = R.pathOr(0, [channel, "totals", metric], totalsByChannel);
    const bottomValue = R.pathOr(0, [channel, "otherTotals", metric], totalsByChannel);

    const parts: BarPart[] = [
      { val: topValue, type: BarType.HORIZONTAL },
      { val: bottomValue, type: BarType.HORIZONTAL_GREY },
    ];

    const numericalDifference = topValue - bottomValue;
    const percentDifference = (numericalDifference / bottomValue) * 100;

    const formattedNumericalDifference = metricFormatter(metric)(numericalDifference);
    const formattedPercentDifference = isFinite(percentDifference)
      ? `(${Math.round(percentDifference)}%)`
      : "";

    const subLabel = `${formattedNumericalDifference} ${formattedPercentDifference}`;

    processedData.push({
      label: channel,
      subLabel,
      topValue,
      bottomValue,
      parts,
      color: channelColors[channel],
    });
  }

  return processedData.sort(
    (a, b) => Math.max(b.topValue, b.bottomValue || 0) - Math.max(a.topValue, a.bottomValue || 0)
  );
};

const PerformanceSnapshot: React.FC<PerformanceSnapshotProps> = React.memo(
  ({ data, otherData, defaultMetric = Metric.CPX }) => {
    const [metric, setMetric] = useState<Metric>(defaultMetric);

    const {
      selectedChannels,
      deliveryAndPerformanceChannels,
      channelColors,
      kpi,
      dates,
      otherDates,
      platformAsSourceOfData,
      dataInfo,
      company,
    } = useContext(CrossChannelContext);
    const metricFormatter = useMemo(() => getMetricFormatter(company), [company]);

    const totalsByChannel = useMemo(
      () => getTotalsByChannel(data, otherData, selectedChannels, deliveryAndPerformanceChannels),
      [data, otherData, selectedChannels, deliveryAndPerformanceChannels]
    );

    const processedData = useMemo(
      () =>
        processSnapshotData(
          totalsByChannel,
          selectedChannels,
          metric,
          channelColors,
          deliveryAndPerformanceChannels,
          metricFormatter
        ),
      [
        totalsByChannel,
        selectedChannels,
        metric,
        channelColors,
        deliveryAndPerformanceChannels,
        metricFormatter,
      ]
    );

    const fileNameIdentifiers = useMemo(() => {
      const prettyMetric =
        PERFORMANCE_METRIC_OPTIONS.find(item => item.value === metric)?.label || metric;

      return [
        "CrossChannel",
        `${dates.start}–${dates.end}`,
        prettyMetric,
        `vs_${otherDates.start}–${otherDates.end}`,
        prettyMetric,
      ];
    }, [metric, dates, otherDates]);

    const excelDownload = useCallback(() => {
      const exportData = processExport(data, otherData);
      exportMultiSheetToExcel(
        [
          { name: "Performance Snapshot", data: exportData },
          { name: "Data Info", data: dataInfo },
        ],
        `${fileNameIdentifiers.join("_")}`
      );
    }, [data, otherData, fileNameIdentifiers, dataInfo]);

    const pngDownload = useCallback(async () => {
      const label = `KPI: ${kpi}`;

      await downloadPNG(".performanceSnapshotContainer", fileNameIdentifiers, label, [
        ".rightSide",
      ]);
    }, [kpi, fileNameIdentifiers]);

    const valueFormatter = useMemo(() => metricFormatter(metric), [metric, metricFormatter]);

    return (
      <div className="performanceSnapshotContainer">
        <ChartContainer
          enableHoverDesign
          title={
            <>
              <div className="primaryDateRange">Primary's</div>
              <Dropdown
                type={DropdownToggleType.WIDGET_TITLE}
                value={metric}
                options={PERFORMANCE_METRIC_OPTIONS}
                onChange={option => setMetric(option)}
              />
            </>
          }
          leftActions={<div className="otherDateRange">vs Comparison</div>}
          rightActions={
            <DownloadDropdown
              size="sm"
              onClickOptions={[excelDownload, pngDownload]}
            ></DownloadDropdown>
          }
          footerContent={[
            `KPI = ${kpi}`,
            `Source = ${platformAsSourceOfData ? "Platform" : "Source of Truth"}`,
          ]}
        >
          <div className="performanceSnapshotGraphContainer">
            <div id="performanceSnapshotGraphContents" className="performanceSnapshotGraphContents">
              {R.isEmpty(processedData) ? (
                <div
                  style={{
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "26px",
                  }}
                >
                  No data to show
                </div>
              ) : (
                <SnapshotChart
                  data={processedData}
                  doubleBarChart={true}
                  valueFormatter={valueFormatter}
                />
              )}
            </div>
          </div>
        </ChartContainer>
      </div>
    );
  }
);

const processExport = (data: CrossChannelMetrics[], otherData: CrossChannelMetrics[]) => {
  let unionData: any[] = [];

  data.forEach(item => {
    let performanceMetrics = {};
    for (let metric of PERFORMANCE_METRICS) {
      performanceMetrics[METRIC_TO_PRETTY_NAME[metric]] = item.metrics[metric];
    }
    unionData.push({
      Channel: item.channel,
      Period: 1,
      Date: item.date,
      ...performanceMetrics,
    });
  });

  otherData.forEach(item => {
    let performanceMetrics = {};
    for (let metric of PERFORMANCE_METRICS) {
      performanceMetrics[METRIC_TO_PRETTY_NAME[metric]] = item.metrics[metric];
    }
    unionData.push({
      Channel: item.channel,
      Period: 2,
      Date: item.date,
      ...performanceMetrics,
    });
  });

  return unionData.sort((a, b) => {
    const channelCompare = a.Channel.localeCompare(b.Channel);
    if (channelCompare !== 0) {
      return channelCompare;
    }

    if (a.Period !== b.Period) {
      return a.Period - b.Period;
    }

    const dateA = new Date(a.Date);
    const dateB = new Date(b.Date);

    return dateA.getTime() - dateB.getTime();
  });
};

export default PerformanceSnapshot;
